import { action, computed, observable } from 'mobx';
import { sleep } from '@linktivity/link-utils';
import { User, UserCredential } from '@/vendors/firebase';
import { Claims } from '@/services/types';

const storage = localStorage;
const ACCESS_TOKEN = 'accessToken';

const MAX_REFRESH_COUNT = 10;

const TIMEOUT = 300;

export class Auth {
  @observable accessor user: User | null = null;
  @observable accessor claims: Claims | null = null;
  @observable accessor accessToken = storage.getItem(ACCESS_TOKEN) || '';

  @action
  setUser(user: User) {
    this.user = user;
    this.getClaims(user);
  }

  @action
  async getClaims(user: User) {
    const tokenResult = await user.getIdTokenResult();
    if (tokenResult) {
      this.setClaims(tokenResult.claims as unknown as Claims);
    }
  }
  @action
  setClaims = (claims: Claims) => {
    this.claims = claims;
  };
  @action
  clearUser() {
    this.user = null;
    try {
      storage.removeItem(ACCESS_TOKEN);
    } finally {
      this.accessToken = '';
    }
  }

  @action
  setToken(token: string) {
    try {
      storage.setItem(ACCESS_TOKEN, token);
    } finally {
      this.accessToken = token;
    }
  }

  @action
  async getToken() {
    if (this.user) {
      const token = await this.user.getIdToken();
      this.setToken(token);
      return token;
    } else {
      return '';
    }
  }

  @action
  getRefreshedToken = async () => {
    let count = 0;
    const requestToken: () => Promise<string> = async () => {
      const token = await this.getToken();
      if (token) {
        return token;
      } else {
        if (count < MAX_REFRESH_COUNT) {
          count++;
          await sleep(TIMEOUT);
          return await requestToken();
        } else {
          return '';
        }
      }
    };
    return await requestToken();
  };

  @action
  updateToken(credential: UserCredential) {
    credential.user.getIdToken().then(token => {
      this.setToken(token);
    });
  }

  @computed
  get loggedIn(): boolean {
    return !!this.accessToken;
  }
}

export default new Auth();
